import { Injectable, InjectionToken, inject } from '@angular/core';
import type * as AmplifyAuth from 'aws-amplify/auth';
import { environment } from '../../../environments/environment';

/** The Amplify calls this app uses. They sit behind a token so tests can replace them. */
export type AmplifyAuthApi = Pick<typeof AmplifyAuth,
  'signIn' | 'confirmSignIn' | 'signOut' | 'fetchAuthSession' | 'resetPassword' | 'confirmResetPassword'>;

/**
 * Amplify is loaded the first time it is needed, so pages that never sign anyone in (the public profiles, the apply
 * form) do not download it. It is configured before its first call.
 */
function lazyAmplify(): AmplifyAuthApi {
  let loading: Promise<typeof AmplifyAuth> | null = null;
  const amplify = () => loading ??= (async () => {
    const [{ Amplify }, auth] = await Promise.all([import('aws-amplify'), import('aws-amplify/auth')]);
    Amplify.configure({
      Auth: { Cognito: { userPoolId: environment.cognito.userPoolId, userPoolClientId: environment.cognito.clientId } },
    });
    return auth;
  })();

  const call = <K extends keyof AmplifyAuthApi>(name: K) =>
    (async (...args: unknown[]) => ((await amplify())[name] as (...a: unknown[]) => unknown)(...args)) as unknown as AmplifyAuthApi[K];

  return {
    signIn:               call('signIn'),
    confirmSignIn:        call('confirmSignIn'),
    signOut:              call('signOut'),
    fetchAuthSession:     call('fetchAuthSession'),
    resetPassword:        call('resetPassword'),
    confirmResetPassword: call('confirmResetPassword'),
  };
}

export const AMPLIFY_AUTH = new InjectionToken<AmplifyAuthApi>('AMPLIFY_AUTH', { providedIn: 'root', factory: lazyAmplify });

export type CognitoErrorKind =
  | 'invalidCredentials' | 'passwordResetRequired' | 'notConfirmed' | 'tooManyAttempts'
  | 'codeMismatch' | 'codeExpired' | 'invalidPassword' | 'network' | 'notConfigured' | 'unsupportedStep' | 'unknown';

/** A Cognito failure reduced to what the screens need to say. */
export class CognitoError extends Error {
  constructor(readonly kind: CognitoErrorKind, message?: string) {
    super(message ?? kind);
  }
}

const KIND_BY_NAME: Record<string, CognitoErrorKind> = {
  NotAuthorizedException:         'invalidCredentials',
  UserNotFoundException:          'invalidCredentials',
  PasswordResetRequiredException: 'passwordResetRequired',
  UserNotConfirmedException:      'notConfirmed',
  LimitExceededException:         'tooManyAttempts',
  TooManyRequestsException:       'tooManyAttempts',
  TooManyFailedAttemptsException: 'tooManyAttempts',
  CodeMismatchException:          'codeMismatch',
  ExpiredCodeException:           'codeExpired',
  InvalidPasswordException:       'invalidPassword',
  NetworkError:                   'network',
};

export function toCognitoError(err: unknown): CognitoError {
  if (err instanceof CognitoError) return err;
  const name = (err as { name?: string } | null)?.name ?? '';
  return new CognitoError(KIND_BY_NAME[name] ?? 'unknown', (err as { message?: string } | null)?.message);
}

/**
 * Signs people in and out of the specialist Cognito pool and hands out their token. Everything else in the app
 * goes through {@link AuthService}; this is the only place that knows about Amplify.
 */
@Injectable({ providedIn: 'root' })
export class CognitoClient {
  private readonly amplify = inject(AMPLIFY_AUTH);

  readonly configured = !!(environment.cognito.userPoolId && environment.cognito.clientId);

  /** 'newPasswordRequired' is the first sign-in of an administrator-created user, who must choose a password. */
  async signIn(email: string, password: string): Promise<'done' | 'newPasswordRequired'> {
    this.requireConfigured();
    try {
      let result;
      try {
        result = await this.amplify.signIn({ username: email, password });
      } catch (err) {
        if ((err as { name?: string }).name !== 'UserAlreadyAuthenticatedException') throw err;
        await this.amplify.signOut();   // a stale session from an earlier visit
        result = await this.amplify.signIn({ username: email, password });
      }
      return this.stepOf(result.nextStep.signInStep);
    } catch (err) {
      throw toCognitoError(err);
    }
  }

  async confirmNewPassword(newPassword: string): Promise<void> {
    this.requireConfigured();
    try {
      const result = await this.amplify.confirmSignIn({ challengeResponse: newPassword });
      if (result.nextStep.signInStep !== 'DONE') throw new CognitoError('unsupportedStep');
    } catch (err) {
      throw toCognitoError(err);
    }
  }

  async signOut(): Promise<void> {
    if (!this.configured) return;
    try { await this.amplify.signOut(); } catch { /* signing out is best effort */ }
  }

  /**
   * The ID token the API accepts, refreshed by Amplify when it is about to expire. Null when there is no session
   * (not signed in, or the refresh token has expired). A failure to reach Cognito is thrown, not reported as "no
   * session", so a brief outage does not sign anyone out.
   */
  async idToken(): Promise<string | null> {
    if (!this.configured) return null;
    const session = await this.amplify.fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  }

  async resetPassword(email: string): Promise<void> {
    this.requireConfigured();
    try { await this.amplify.resetPassword({ username: email }); } catch (err) { throw toCognitoError(err); }
  }

  async confirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
    this.requireConfigured();
    try {
      await this.amplify.confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    } catch (err) {
      throw toCognitoError(err);
    }
  }

  private stepOf(step: string): 'done' | 'newPasswordRequired' {
    if (step === 'DONE') return 'done';
    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') return 'newPasswordRequired';
    // Amplify reports these two as steps, not errors
    if (step === 'RESET_PASSWORD') throw new CognitoError('passwordResetRequired');
    if (step === 'CONFIRM_SIGN_UP') throw new CognitoError('notConfirmed');
    throw new CognitoError('unsupportedStep');
  }

  private requireConfigured(): void {
    if (!this.configured) throw new CognitoError('notConfigured');
  }
}
